import { useQueryClient, InfiniteData } from "@tanstack/react-query";
import { useCallback } from "react";
import { NoInfer } from "@tanstack/react-table";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import { deepMerge, DeepPartial } from "@/lib/object";
import {
  CardCollectionPagedResult,
  cardCollectionQueryKey,
} from "./useCardCollection";
import type { CardCollectionRow } from "../types";

type CardAction = "added" | "removed" | "none";
type CardCollectionInfiniteData = InfiniteData<CardCollectionPagedResult>;
type CardCollectionSummary = NonNullable<
  CardCollectionPagedResult["extra"]
>["summary"];

interface RowUpdateResult {
  row: CardCollectionRow;
  cardAction: CardAction;
}

function updateMatchingRow(
  row: CardCollectionRow,
  patientId: string,
  newRow: DeepPartial<CardCollectionRow>,
): RowUpdateResult {
  if (row.patient.patientId !== patientId) {
    return { row, cardAction: "none" };
  }

  // Only patches that actually touch `cardOnFile` can change the card state.
  // A patch that doesn't include the key (e.g. the SMS card-capture flow which
  // only sets `cardCapture`) must not be interpreted as adding/removing a card.
  const touchesCardOnFile =
    newRow.patient != null && "cardOnFile" in newRow.patient;

  const hadCard = row.patient.cardOnFile !== null;
  const hasCard = touchesCardOnFile
    ? newRow.patient?.cardOnFile != null
    : hadCard;

  let cardAction: CardAction = "none";
  if (hadCard && !hasCard) cardAction = "removed";
  if (!hadCard && hasCard) cardAction = "added";

  return { row: deepMerge<CardCollectionRow>(row, newRow), cardAction };
}

function getUpdatedNoCardCount(
  noCardCount: number | undefined,
  cardAction: CardAction,
): number | undefined {
  switch (cardAction) {
    case "removed":
      return (noCardCount ?? 0) + 1;
    case "added":
      return (noCardCount ?? 0) - 1;
    case "none":
      return noCardCount;
  }
}

function buildSummary(
  summary: CardCollectionSummary,
  cardAction: CardAction,
): CardCollectionSummary {
  return (
    summary && {
      totalCount: summary.totalCount,
      noCardCount: getUpdatedNoCardCount(summary.noCardCount, cardAction),
    }
  );
}

function isCardCollectionRow(
  row: CardCollectionRow | null,
): row is CardCollectionRow {
  return row !== null;
}

function updatePageItems(
  items: CardCollectionRow[],
  patientId: string,
  newRow: DeepPartial<CardCollectionRow>,
  removeWithCard: boolean,
  onCardAction: (cardAction: CardAction) => void,
): CardCollectionRow[] {
  return items
    .map((row) => {
      const updated = updateMatchingRow(row, patientId, newRow);

      if (updated.cardAction !== "none") onCardAction(updated.cardAction);

      const shouldRemove = updated.cardAction === "added" && removeWithCard;
      return shouldRemove ? null : updated.row;
    })
    .filter(isCardCollectionRow);
}

function updatePages(
  pages: CardCollectionPagedResult[],
  patientId: string,
  newRow: DeepPartial<CardCollectionRow>,
  removeWithCard: boolean,
): CardCollectionPagedResult[] {
  const firstPageExtra = pages[0]?.extra;
  let cardAction: CardAction = "none";

  const pagesWithUpdatedItems = pages.map((page) => ({
    ...page,
    items: updatePageItems(
      page.items,
      patientId,
      newRow,
      removeWithCard,
      (action) => (cardAction = action),
    ),
  }));

  const updatedExtra = firstPageExtra && {
    ...firstPageExtra,
    summary: buildSummary(firstPageExtra.summary, cardAction),
  };

  return pagesWithUpdatedItems.map((page) => ({
    ...page,
    extra: updatedExtra,
  }));
}

function updateCardCollectionInfiniteData(
  data: NoInfer<CardCollectionInfiniteData> | undefined,
  patientId: string,
  newRow: DeepPartial<CardCollectionRow>,
  removeWithCard: boolean,
): CardCollectionInfiniteData | undefined {
  if (!data) return data;

  return {
    ...data,
    pages: updatePages(data.pages, patientId, newRow, removeWithCard),
  };
}

export function useUpdateCardCollectionCacheEntry() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantRouteContext();

  return useCallback(
    (patientId: string, newRow: DeepPartial<CardCollectionRow>) => {
      for (const hasCard of [undefined, false] as const) {
        queryClient.setQueryData<CardCollectionInfiniteData>(
          cardCollectionQueryKey(tenantId, hasCard),
          (prev) =>
            updateCardCollectionInfiniteData(
              prev,
              patientId,
              newRow,
              hasCard === false,
            ),
        );
      }
    },
    [tenantId, queryClient],
  );
}

export function useUpdateCardCollectionCache(patientId: string) {
  const updateEntry = useUpdateCardCollectionCacheEntry();

  return useCallback(
    (newRow: DeepPartial<CardCollectionRow>) => updateEntry(patientId, newRow),
    [patientId, updateEntry],
  );
}
